import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Map } from 'immutable';
import cn from 'classnames';
import axios from 'axios';
import moment from 'moment-timezone';
import styles from './attendees.scss';

/**
 * Generate initials from a name
 */
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

/**
 * Format event date with timezone
 */
const formatEventDate = (eventData) => {
  const { start_date, end_date, all_day_event, timezone } = eventData;

  if (all_day_event) {
    return moment.tz(start_date, timezone).format('MMM D, YYYY');
  }

  const startMoment = moment.tz(start_date, timezone);
  const endMoment = moment.tz(end_date, timezone);

  if (startMoment.isSame(endMoment, 'day')) {
    return `${startMoment.format('MMM D, YYYY h:mm A')} - ${endMoment.format('h:mm A z')}`;
  }

  return `${startMoment.format('MMM D, YYYY h:mm A')} - ${endMoment.format('MMM D, YYYY h:mm A z')}`;
};

/**
 * Avatar component with initials fallback
 */
function Avatar({ name, avatarUrl, size = 40 }) {
  const [imageError, setImageError] = useState(false);

  if (avatarUrl && !imageError) {
    return (
      <img src={avatarUrl} alt={name} className={styles.avatarImage} style={{ width: size, height: size }} onError={() => setImageError(true)} />
    );
  }

  return (
    <div className={styles.avatarInitials} style={{ width: size, height: size, fontSize: size * 0.4 }}>
      {getInitials(name)}
    </div>
  );
}

Avatar.propTypes = {
  name: PropTypes.string.isRequired,
  avatarUrl: PropTypes.string,
  size: PropTypes.number,
};

/**
 * Attendees Modal Component
 */
function AttendeesModal({ event, settings, onClose }) {
  const [attendees, setAttendees] = useState([]);
  const [filteredAttendees, setFilteredAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSeries, setIsSeries] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');

  useEffect(() => {
    const fetchAttendees = async () => {
      try {
        setLoading(true);
        const eventId = event.get('id');
        const baseUrl = process.env.EVENTVIEWER_SERVER || '';
        const response = await axios.get(`${baseUrl}/api/v1/event-page/attendees/events/${eventId}`);
        const { data } = response;

        setIsSeries(data.is_series || false);
        setEvents(data.events || []);

        // Function to make attendees unique
        const makeUnique = (attendeesList) => {
          const uniqueAttendees = [];
          const seen = new Set();

          attendeesList.forEach((attendee) => {
            const identifier = attendee.email || `${attendee.first_name}-${attendee.last_name}`;
            if (!seen.has(identifier)) {
              seen.add(identifier);
              uniqueAttendees.push(attendee);
            }
          });

          return uniqueAttendees;
        };

        // If series, find the most upcoming event
        if (data.is_series && data.events && data.events.length > 0) {
          const now = new Date();
          const upcomingEvent = data.events
            .filter((e) => new Date(e.start_date) >= now)
            .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))[0];

          const defaultEventId = upcomingEvent ? upcomingEvent.id : data.events[0].id;
          setSelectedEventId(defaultEventId);

          // Filter attendees for the selected event and make unique
          const eventAttendees = (data.attendees || []).filter((att) => att.event.id === defaultEventId);
          const uniqueEventAttendees = makeUnique(eventAttendees);
          setAttendees(uniqueEventAttendees);
          setFilteredAttendees(uniqueEventAttendees);
        } else {
          // Not a series, use all attendees and make unique
          const attendeesList = data.attendees || [];
          const uniqueAttendees = makeUnique(attendeesList);
          setAttendees(uniqueAttendees);
          setFilteredAttendees(uniqueAttendees);
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching attendees:', err);
        setError('Failed to load attendees');
      } finally {
        setLoading(false);
      }
    };

    fetchAttendees();
  }, [event]);

  // Handle event selection change
  const handleEventChange = (eventId) => {
    setSelectedEventId(eventId);
    setSearchQuery(''); // Reset search when changing events

    // Filter attendees for the selected event
    const eventAttendees = attendees.filter((att) => att.event.id === eventId);
    setFilteredAttendees(eventAttendees);
  };

  // Handle search
  useEffect(() => {
    const currentAttendees = isSeries ? attendees.filter((att) => att.event.id === selectedEventId) : attendees;

    if (!searchQuery.trim()) {
      setFilteredAttendees(currentAttendees);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = currentAttendees.filter((attendee) => {
        const fullName = `${attendee.first_name || ''} ${attendee.last_name || ''}`.trim().toLowerCase();
        return fullName.includes(query);
      });
      setFilteredAttendees(filtered);
    }
  }, [searchQuery, attendees, selectedEventId, isSeries]);

  const labelText = settings.get('attendeesText', 'Attendees');

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={cn(styles.modalContent, styles.attendeesModal)} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>{labelText}</h2>
          <button className={styles.modalClose} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {!loading && !error && isSeries && events.length > 0 && (
          <div className={styles.eventSelector}>
            <label htmlFor="event-select">Select Event:</label>
            <select id="event-select" value={selectedEventId} onChange={(e) => handleEventChange(e.target.value)} className={styles.eventSelect}>
              {events.map((evt) => (
                <option key={evt.id} value={evt.id}>
                  {formatEventDate(evt)}
                </option>
              ))}
            </select>
          </div>
        )}

        {!loading && !error && attendees.length > 0 && (
          <div className={styles.searchContainer}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <svg className={styles.searchIcon} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M9 17C13.4183 17 17 13.4183 17 9C17 4.58172 13.4183 1 9 1C4.58172 1 1 4.58172 1 9C1 13.4183 4.58172 17 9 17Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path d="M19 19L14.65 14.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        )}

        <div className={styles.modalBody}>
          {loading && (
            <div className={styles.modalLoading}>
              <div className={styles.spinner} />
              <p>Loading attendees...</p>
            </div>
          )}

          {error && (
            <div className={styles.modalError}>
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && attendees.length === 0 && (
            <div className={styles.modalEmpty}>
              <p>No attendees found</p>
            </div>
          )}

          {!loading && !error && attendees.length > 0 && filteredAttendees.length === 0 && (
            <div className={styles.modalEmpty}>
              <p>No attendees match your search</p>
            </div>
          )}

          {!loading && !error && filteredAttendees.length > 0 && (
            <div className={styles.attendeesList}>
              {filteredAttendees.map((attendee) => {
                const fullName = `${attendee.first_name || ''} ${attendee.last_name || ''}`.trim() || 'Unknown';
                return (
                  <div key={attendee.order_id || attendee.id} className={styles.attendeeCard}>
                    <Avatar name={fullName} avatarUrl={attendee.avatar_url} size={48} />
                    <div className={styles.attendeeName}>{fullName}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

AttendeesModal.propTypes = {
  event: PropTypes.instanceOf(Map).isRequired,
  settings: PropTypes.instanceOf(Map).isRequired,
  onClose: PropTypes.func.isRequired,
};

/**
 * Attendees component displays attendee count information
 * Supports visibility and text customization via settings
 */
function Attendees({ event, settings, className = '' }) {
  const [showModal, setShowModal] = useState(false);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);

  // Check if component should be shown
  if (!settings.get('showAttendees')) {
    return null;
  }

  useEffect(() => {
    const fetchAttendees = async () => {
      try {
        setLoading(true);
        const eventId = event.get('id');
        const baseUrl = process.env.EVENTVIEWER_SERVER || '';
        const response = await axios.get(`${baseUrl}/api/v1/event-page/attendees/events/${eventId}`);
        const { data } = response;

        // Make attendees unique by email or name
        const uniqueAttendees = [];
        const seen = new Set();

        (data.attendees || []).forEach((attendee) => {
          const identifier = attendee.email || `${attendee.first_name}-${attendee.last_name}`;
          if (!seen.has(identifier)) {
            seen.add(identifier);
            uniqueAttendees.push(attendee);
          }
        });

        setAttendees(uniqueAttendees);
      } catch (err) {
        console.error('Error fetching attendees:', err);
        setAttendees([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendees();
  }, [event]);

  // Show loading state
  if (loading) {
    return (
      <div className={cn(styles.eventAttendees, className)}>
        <div className={styles.attendeesLabel}>
          <i className="fas fa-users" style={{ marginRight: '8px' }} />
          Loading Attendee List...
        </div>
      </div>
    );
  }

  // If no attendees, don't render
  if (!attendees || attendees.length === 0) {
    return null;
  }

  const labelText = settings.get('attendeesText', 'Attendees');
  const attendeeCount = attendees.length;
  const peopleText = attendeeCount === 1 ? 'person' : 'people';
  const actionText = event.get('is_past_event') ? 'attended' : 'registered';

  // Get first 3 attendees for avatar display
  const displayAttendees = attendees.slice(0, 3);

  return (
    <>
      <div
        className={cn(styles.eventAttendees, styles.clickable, className)}
        onClick={() => setShowModal(true)}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setShowModal(true);
          }
        }}
      >
        <div className={styles.attendeesInfo}>
          <div className={styles.attendeesLabel}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
              <path
                d="M5.39995 7.9998C6.08951 7.9998 6.75083 7.72588 7.23843 7.23828C7.72602 6.75069 7.99995 6.08937 7.99995 5.3998C7.99995 4.71024 7.72602 4.04892 7.23843 3.56133C6.75083 3.07373 6.08951 2.7998 5.39995 2.7998C4.71039 2.7998 4.04907 3.07373 3.56147 3.56133C3.07388 4.04892 2.79995 4.71024 2.79995 5.3998C2.79995 6.08937 3.07388 6.75069 3.56147 7.23828C4.04907 7.72588 4.71039 7.9998 5.39995 7.9998ZM9.94955 12.1158C10.3736 12.2894 10.9128 12.3998 11.5992 12.3998C14.7992 12.3998 14.7992 9.9998 14.7992 9.9998C14.7992 9.68168 14.6728 9.37658 14.448 9.15156C14.2231 8.92654 13.9181 8.80002 13.6 8.7998H9.89755C10.212 9.1806 10.4008 9.6678 10.4008 10.1998V10.4846C10.3991 10.552 10.3945 10.6192 10.3872 10.6862C10.3327 11.186 10.1837 11.671 9.94955 12.1158ZM13.6 5.9998C13.6 6.53024 13.3892 7.03895 13.0142 7.41402C12.6391 7.78909 12.1304 7.9998 11.6 7.9998C11.0695 7.9998 10.5608 7.78909 10.1857 7.41402C9.81067 7.03895 9.59995 6.53024 9.59995 5.9998C9.59995 5.46937 9.81067 4.96066 10.1857 4.58559C10.5608 4.21052 11.0695 3.9998 11.6 3.9998C12.1304 3.9998 12.6391 4.21052 13.0142 4.58559C13.3892 4.96066 13.6 5.46937 13.6 5.9998ZM1.19995 10.3998C1.19995 9.97546 1.36852 9.56849 1.66858 9.26843C1.96864 8.96838 2.3756 8.7998 2.79995 8.7998H7.99995C8.4243 8.7998 8.83126 8.96838 9.13132 9.26843C9.43138 9.56849 9.59995 9.97546 9.59995 10.3998C9.59995 10.3998 9.59995 13.5998 5.39995 13.5998C1.19995 13.5998 1.19995 10.3998 1.19995 10.3998Z"
                fill="#181818"
              />
            </svg>
            {labelText}
          </div>
          <div className={styles.attendeesContent}>
            <span className={styles.attendeeCount}>
              {attendeeCount} {peopleText} {actionText}
            </span>
          </div>
        </div>
        <div className={styles.avatarsContainer}>
          {displayAttendees.map((attendee, index) => {
            const fullName = `${attendee.first_name || ''} ${attendee.last_name || ''}`.trim() || 'Unknown';
            return <Avatar key={attendee.order_id || index} name={fullName} avatarUrl={attendee.avatar_url} size={36} />;
          })}
        </div>
      </div>

      {showModal && <AttendeesModal event={event} settings={settings} onClose={() => setShowModal(false)} />}
    </>
  );
}

Attendees.propTypes = {
  event: PropTypes.instanceOf(Map).isRequired,
  settings: PropTypes.instanceOf(Map).isRequired,
  className: PropTypes.string,
};

export default Attendees;
